import { css, styled } from 'styled-components';

interface RootProps {
    $maxWidth?: string;
    $minWidth?: string;
    $width?: string;
    $flex: boolean;
    $tight: boolean;
    $verticalPadding?: string;
    $divider: boolean;
    $align?: string;
    $justify?: string;
    $noMargin?: boolean;
}

const flexGroup = css`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

export const Root = styled.div<RootProps>`
    /* Base spacing */
    margin-left: ${({ theme }) => theme.spacing(12)};
    margin-right: ${({ theme }) => theme.spacing(12)};
    margin-top: ${({ theme }) => theme.spacing(12)};
    margin-bottom: ${({ theme }) => theme.spacing(13)};

    /* Use padding for vertical spacing when verticalPadding is set */
    ${({ $verticalPadding }) =>
        $verticalPadding &&
        css`
            padding-top: ${$verticalPadding};
            padding-bottom: ${$verticalPadding};
            margin-top: 0;
            margin-bottom: 0;
        `}

    ${({ $noMargin }) =>
        $noMargin &&
        css`
            margin: 0;
        `}

    /* Width constraints */
  ${({ $maxWidth }) =>
        $maxWidth &&
        css`
            max-width: ${$maxWidth};
        `}
  ${({ $minWidth }) =>
        $minWidth &&
        css`
            min-width: ${$minWidth};
        `}
  ${({ $width }) =>
        $width &&
        css`
            width: ${$width};
        `}

  /* Flex behavior */
  ${({ $flex }) => $flex && flexGroup}

  /* Optional divider */
  ${({ $divider, theme }) =>
        $divider &&
        css`
            position: relative;
            padding-bottom: ${theme.spacing(4)};
            margin-bottom: ${theme.spacing(13)} !important;

            &:after {
                content: '';
                position: absolute;
                bottom: 0;
                left: 0;
                width: 100%;
                height: 1px;
                background: ${theme.groupDivider};
            }
        `}

    /* 1) Big gap under any direct child that isn’t a group wrapper */
    & > :not([data-group]) {
        margin-bottom: ${({ theme }) => theme.spacing(13)};
        &:last-child {
            margin-bottom: 0;
        }
    }

    /* 2a) Clear _all_ margins on each nested Group wrapper */
    & > [data-group] {
        margin: 0 !important;
    }

    /* 2b) Then re-introduce the large gap _between_ those wrapper siblings */
    & > [data-group]:not(:last-child) {
        margin-bottom: ${({ theme }) => theme.spacing(13)} !important;
    }

    /* 3) Small gap _inside_ each wrapper for its non-Group children */
    & > [data-group] > :not([data-group]) {
        margin-bottom: ${({ theme }) => theme.spacing(4)};
        &:last-child {
            margin-bottom: 0;
        }
    }

    /* 4a) Clear _all_ margins on ANY group-wrapper inside another group-wrapper */
    & [data-group] > [data-group] {
        margin: 0 !important;
    }

    /* 4b) Then re-introduce the small gap _between_ those nested wrapper siblings */
    & [data-group] > [data-group]:not(:last-child) {
        margin-bottom: ${({ theme }) => theme.spacing(4)} !important;
    }

    /* Alignment */
    ${({ $align }) =>
        $align &&
        css`
            align-items: ${$align};
        `}
    ${({ $justify }) =>
        $justify &&
        css`
            justify-content: ${$justify};
        `}
`;

export const TightRoot = styled(Root)<RootProps>`
    ${({ $noMargin }) =>
        $noMargin &&
        css`
            margin: 0;
        `}

    & > * {
        margin-bottom: ${({ theme }) => theme.spacing(2)} !important;
        &:last-child {
            margin-bottom: 0 !important;
        }
    }

    /* Mobile adjustments */
    @media (max-width: 600px) {
        & > * {
            margin-bottom: ${({ theme }) => theme.spacing(1)} !important;
            &:last-child {
                margin-bottom: 0 !important;
            }
        }
    }
`;
